import { inject, Injectable } from '@angular/core';
import { createEffect } from '@ngrx/effects';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { combineLatest, EMPTY, of } from 'rxjs';

import { Store } from '@ngrx/store';
import { ELIGIBLE_PROMOTIONS_STORE_API_ACTIONS } from '@store/eligible-promotions/eligible-promotions.action';
import { BogofApiService } from '@core/services/api/bogof/bogof-api.service';
import { statedStream } from '@shared/utils/rxjs/stated-steam';
import { statedDataToAction } from '@shared/utils/rxjs/stated-data-to-action';
import { DoodApiError } from '@shared/interfaces/error.interface';
import { DoodCartModel } from '@core/models/cart.model';
import { OrdersService } from '@core/services/orders/orders.service';

import { SELECT_SHOP_ACTIVE, SELECT_SHOP_CURRENT_PAGE_ID } from '@store/shop';
import { SELECT_ORDER_TYPE } from '@store/settings';
import { CartStoreSelector } from '@common/selectors/cart.selector';
import { CartKeys } from '@config/keys/cart.keys';

/**
 * Shop-scoped carts are stored as `shop_${shopId}`. A marketplace cart stays active
 * while the visitor browses several shops, so it is always eligible.
 */
export function activeCartMatchesDisplayedShop(
  cart: { [CartKeys.MultiShop]?: boolean; [CartKeys.Handle]?: string | null },
  shopId: string | undefined,
): boolean {
  if (cart[CartKeys.MultiShop] === true) {
    return true;
  }
  return !!shopId && cart[CartKeys.Handle] === `shop_${shopId}`;
}

@Injectable({
  providedIn: 'root',
})
export class EligiblePromotionsEffects {
  private readonly store = inject(Store);
  private readonly bogofApiService = inject(BogofApiService);
  private readonly ordersService = inject(OrdersService);
  private readonly cartStoreSelector = inject(CartStoreSelector);

  loadEligiblePromotionsEffect = createEffect(() => {
    return combineLatest({
      shop: this.store.select(SELECT_SHOP_ACTIVE),
      // Cleared at the start of a shop navigation, before the next shop entity arrives.
      currentPageShopId: this.store.select(SELECT_SHOP_CURRENT_PAGE_ID),
      distributionMode: this.store.select(SELECT_ORDER_TYPE),
      cart: this.cartStoreSelector.selectActive$,
    }).pipe(
      filter(
        ({ shop, distributionMode, cart }) =>
          !!shop &&
          !!distributionMode &&
          !!cart &&
          typeof cart.multi_shop === 'boolean' &&
          !!cart.marketplace,
      ),
      distinctUntilChanged(
        (prev, curr) =>
          prev.shop?.id === curr.shop?.id &&
          prev.currentPageShopId === curr.currentPageShopId &&
          prev.distributionMode === curr.distributionMode &&
          prev.cart?.[CartKeys.Handle] === curr.cart?.[CartKeys.Handle] &&
          prev.cart?.[CartKeys.Type] === curr.cart?.[CartKeys.Type],
      ),
      switchMap(({ shop, distributionMode, cart, currentPageShopId }) => {
        // Marketplace cart stays valid across shops. Keep its promotions while the
        // next shop page is still resolving.
        if (cart?.multi_shop === true && !currentPageShopId) {
          return EMPTY;
        }
        // Page shop is cleared as soon as navigation starts. The active shop entity
        // (and its cart) catch up only after the shop request, which left the
        // previous promotions on screen for the whole wait.
        if (cart != null && !activeCartMatchesDisplayedShop(cart, currentPageShopId ?? undefined)) {
          return of(
            ELIGIBLE_PROMOTIONS_STORE_API_ACTIONS.loadEligiblePromotionsSuccess({ data: [] }),
          );
        }

        const order =
          cart != null
            ? this.ordersService.mapCartToOrderValues(cart as DoodCartModel, [])
            : { type: distributionMode, shop: shop?.id };

        if (order == null) {
          return of(
            ELIGIBLE_PROMOTIONS_STORE_API_ACTIONS.loadEligiblePromotionsSuccess({ data: [] }),
          );
        }
        return statedDataToAction(
          statedStream(
            this.bogofApiService.getEligibleProductsByOrder$(order).pipe(map(list => list ?? [])),
          ),
          {
            loading: () => ELIGIBLE_PROMOTIONS_STORE_API_ACTIONS.loadEligiblePromotionsLoading(),
            failure: (error: DoodApiError) =>
              ELIGIBLE_PROMOTIONS_STORE_API_ACTIONS.loadEligiblePromotionsFailure({ error }),
            success: data =>
              ELIGIBLE_PROMOTIONS_STORE_API_ACTIONS.loadEligiblePromotionsSuccess({ data }),
          },
        );
      }),
    );
  });
}
